import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import { shippingAPI, adminAPI, customerAPI } from '../../services/api';
import { LuPlus, LuTrash2, LuSearch, LuTriangleAlert, LuTruck, LuBox, LuChevronDown, LuChevronRight, LuPackage, LuMapPin } from 'react-icons/lu';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { COUNTRIES, getStatesForCountry, validatePhoneNumber, formatPhoneNumber, validatePostalCode } from '../../utils/addressData';
import api from '../../services/api';

const ShippingForm = ({ formik, isAdmin = false }) => {
  // Warehouse state (implicit default)
  const [resolvedWarehouse, setResolvedWarehouse] = useState(null);
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [showWarehousePicker, setShowWarehousePicker] = useState(false);

  // Address state
  const [destinationAddresses, setDestinationAddresses] = useState([]);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [newAddressForm, setNewAddressForm] = useState({
    name: '', street_address: '', address_line_2: '', city: '',
    state_province: '', postal_code: '', country: 'Canada',
    contact_name: '', contact_phone: '', notes: ''
  });
  const [phoneError, setPhoneError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');

  // Item state
  const [showAddItem, setShowAddItem] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pickerSelections, setPickerSelections] = useState({});
  const [currentItem, setCurrentItem] = useState({ productId: null, productSku: '', productName: '', quantity: 1, specificSerials: [], specificMacs: [], specificAssetIds: [], notes: '' });

  // Identifier picker state
  const [showSerialPicker, setShowSerialPicker] = useState(false);
  const [showMacPicker, setShowMacPicker] = useState(false);
  const [availableSerials] = useState([]);
  const [availableMacs] = useState([]);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [selectedMacs, setSelectedMacs] = useState([]);

  // Editing identifiers on an already-added line item
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [editingLineSerials, setEditingLineSerials] = useState([]);
  const [editingLineMacs, setEditingLineMacs] = useState([]);
  const [editingLineAvailableSerials, setEditingLineAvailableSerials] = useState([]);
  const [editingLineAvailableMacs, setEditingLineAvailableMacs] = useState([]);
  const [editingPickerSearch, setEditingPickerSearch] = useState('');
  const [editingPickerType, setEditingPickerType] = useState(null); // 'serial' or 'mac'

  // Shipment group asset assignment
  const [groupAssetPicker, setGroupAssetPicker] = useState(null); // { groupIndex, giIdx, lineIndex }
  const [groupPickerSerials, setGroupPickerSerials] = useState([]);
  const [groupPickerMacs, setGroupPickerMacs] = useState([]);
  const [groupPickerAvailableSerials, setGroupPickerAvailableSerials] = useState([]);
  const [groupPickerAvailableMacs, setGroupPickerAvailableMacs] = useState([]);
  const [groupPickerSearch, setGroupPickerSearch] = useState('');

  // Shipment groups (progressive — hidden by default)
  const [showGroups, setShowGroups] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [pickerSearch, setPickerSearch] = useState('');

  // Project autocomplete suggestions (shared across all groups)
  const [projectSuggestions, setProjectSuggestions] = useState([]);
  const projectFetchTimer = useRef(null);
  const datalistId = useRef('project-suggestions-' + Math.random().toString(36).slice(2, 8)).current;

  const fetchProjectSuggestions = useCallback((search) => {
    if (projectFetchTimer.current) clearTimeout(projectFetchTimer.current);
    if (!search || search.length < 2) {
      setProjectSuggestions([]);
      return;
    }
    projectFetchTimer.current = setTimeout(async () => {
      try {
        const result = await customerAPI.getProjects(search);
        setProjectSuggestions(result.data || []);
      } catch {
        // Autocomplete is best-effort; silently ignore errors.
      }
    }, 300);
  }, []);

  const companyId = formik.values.companyId;
  const isPickup = formik.values.shippingMethod === 'pickup';
  const isFreight = formik.values.shippingMethod === 'freight';
  const outboundType = isPickup ? 'pickup' : 'ship';

  const normalizeAddressResults = (result) => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.addresses)) return result.addresses;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.data?.addresses)) return result.data.addresses;
    return [];
  };

  // --- Auto-resolve warehouse ---
  useEffect(() => {
    if (!companyId) return;
    const resolveWarehouse = async () => {
      try {
        const result = await shippingAPI.getDefaultWarehouse(companyId);
        if (result.warehouse) {
          setResolvedWarehouse(result.warehouse);
          formik.setFieldValue('sourceWarehouseId', result.warehouse.id);
        }
        if (result.warehouses) {
          setAllWarehouses(result.warehouses);
          // Show picker if multiple warehouses
          if (result.source === 'first' && result.warehouses.length > 1) {
            setShowWarehousePicker(false); // Don't auto-show, just make it available
          }
        }
      } catch (err) {
        console.error('Error resolving warehouse:', err);
      }
    };
    resolveWarehouse();
  }, [companyId]);

  // --- Fetch destination addresses ---
  useEffect(() => {
    if (!companyId) return;
    const fetchAddresses = async () => {
      try {
        const result = await shippingAPI.getCustomerAddresses(companyId);
        setDestinationAddresses(normalizeAddressResults(result));
      } catch (err) {
        console.error('Error fetching addresses:', err);
      }
    };
    fetchAddresses();
  }, [companyId]);

  // --- Product search ---
  const { data: productSuggestions } = useQuery(
    ['productSuggestions', searchTerm, companyId, resolvedWarehouse?.id],
    async () => {
      if (!companyId) return [];
      const response = await api.get('/inventory/search', {
        params: { query: searchTerm, warehouseId: resolvedWarehouse?.id },
        headers: { 'Customer-ID': companyId }
      });
      return response.data || [];
    },
    { enabled: showAddItem && !!companyId && (!searchTerm || searchTerm.length > 1), keepPreviousData: true }
  );

  // --- Calculate totals ---
  const lineItems = formik.values.lineItems || [];
  const totalWeight = lineItems.reduce((sum, item) => sum + (parseFloat(item.weight || 0) * parseFloat(item.quantity || 0)), 0);
  const totalVolume = lineItems.reduce((sum, item) => sum + (parseFloat(item.volume || 0) * parseFloat(item.quantity || 0)), 0);
  const recommendFreight = totalVolume > 10 || totalWeight > 150;

  // Filter serials/MACs by project selection
  const projectFilter = currentItem.projectFilter;
  const filteredSerials = projectFilter
    ? availableSerials.filter(s => s.project === projectFilter)
    : availableSerials;
  const filteredMacs = projectFilter
    ? availableMacs.filter(m => m.project === projectFilter)
    : availableMacs;

  useEffect(() => {
    formik.setFieldValue('estimated_weight_lbs', totalWeight);
    formik.setFieldValue('estimated_volume_cuft', totalVolume);
  }, [totalWeight, totalVolume]);

  useEffect(() => {
    if (!isPickup) return;

    if (formik.values.destinationAddressId) {
      formik.setFieldValue('destinationAddressId', '');
    }

    if (
      formik.values.destinationAddress?.street ||
      formik.values.destinationAddress?.city ||
      formik.values.destinationAddress?.state ||
      formik.values.destinationAddress?.postalCode ||
      formik.values.destinationAddress?.country
    ) {
      formik.setFieldValue('destinationAddress', {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      });
    }

    if (showAddAddress) setShowAddAddress(false);
  }, [isPickup]);

  useEffect(() => {
    if (isFreight) return;

    if (formik.values.liftgateRequired) formik.setFieldValue('liftgateRequired', false);
    if (formik.values.whiteGlove) formik.setFieldValue('whiteGlove', false);
    if (formik.values.inside_delivery) formik.setFieldValue('inside_delivery', false);
  }, [isFreight, formik.values.liftgateRequired, formik.values.whiteGlove, formik.values.inside_delivery]);

  // --- Handlers ---
  const handleWarehouseChange = (warehouseId) => {
    const wh = allWarehouses.find(w => w.id === warehouseId);
    if (wh) {
      setResolvedWarehouse(wh);
      formik.setFieldValue('sourceWarehouseId', wh.id);
      setShowWarehousePicker(false);
    }
  };

  const handleOutboundTypeChange = (type) => {
    if (type === 'pickup') {
      formik.setFieldValue('shippingMethod', 'pickup');
      return;
    }

    if (formik.values.shippingMethod === 'pickup') {
      formik.setFieldValue('shippingMethod', '');
    }
  };

  const handleShippingMethodChange = (method) => {
    formik.setFieldValue('shippingMethod', method);

    if (method !== 'freight') {
      formik.setFieldValue('liftgateRequired', false);
      formik.setFieldValue('whiteGlove', false);
      formik.setFieldValue('inside_delivery', false);
    }
  };

  const handleDestinationSelect = (addressId) => {
    formik.setFieldValue('destinationAddressId', addressId);
    const addr = destinationAddresses.find(a => a.id === addressId);
    if (addr) {
      formik.setFieldValue('destinationAddress', {
        name: addr.name,
        street: addr.street_address,
        addressLine2: addr.address_line_2 || '',
        city: addr.city,
        state: addr.state_province,
        postalCode: addr.postal_code,
        country: addr.country,
        contactName: addr.contact_name || '',
        contactPhone: addr.contact_phone || ''
      });
    }
  };

  const getProductAvailableQuantity = (product) => product?.availableQuantity ?? product?.quantity ?? 0;

  const resetItemPicker = () => {
    setShowAddItem(false);
    setSearchTerm('');
    setPickerSelections({});
    setCurrentItem({ productId: null, productSku: '', productName: '', quantity: 1, specificSerials: [], specificMacs: [], specificAssetIds: [], notes: '' });
  };

  const togglePickerSelection = (product) => {
    setPickerSelections(prev => {
      if (prev[product.id]) {
        const next = { ...prev };
        delete next[product.id];
        return next;
      }

      const availableQuantity = getProductAvailableQuantity(product);
      return {
        ...prev,
        [product.id]: {
          product,
          quantity: availableQuantity > 0 ? 1 : 0
        }
      };
    });
  };

  const updatePickerQuantity = (product, quantity) => {
    const availableQuantity = getProductAvailableQuantity(product);
    const normalizedQuantity = Math.max(0, Math.min(parseInt(quantity, 10) || 0, availableQuantity || 0));

    setPickerSelections(prev => ({
      ...prev,
      [product.id]: {
        product,
        quantity: normalizedQuantity
      }
    }));
  };

  const handleAddSelectedItems = () => {
    const selectedItems = Object.values(pickerSelections)
      .filter(({ product, quantity }) => product && quantity > 0);

    if (selectedItems.length === 0) return;

    const nextLineItems = [...lineItems];

    selectedItems.forEach(({ product, quantity }) => {
      const existingIndex = nextLineItems.findIndex(item => item.productId === product.id);
      const lineItem = {
        productId: product.id,
        productSku: product.productSku,
        productName: product.productName,
        quantity,
        specificSerials: [],
        specificMacs: [],
        specificAssetIds: [],
        notes: '',
        weight: product.weightLbs || product.weight_lbs || 0,
        itemDimensions: product.individualDimensions || {
          length: product.length || '',
          width: product.width || '',
          height: product.height || '',
          weight: product.weightLbs || product.weight_lbs || ''
        },
        cartonDimensions: product.cartonDimensions || {
          length: product.cartonLength || product.carton_length || '',
          width: product.cartonWidth || product.carton_width || '',
          height: product.cartonHeight || product.carton_height || '',
          weight: product.cartonWeight || product.carton_weight || ''
        },
        volume: product.volume || 0
      };

      if (existingIndex >= 0) {
        const existingItem = nextLineItems[existingIndex];
        const availableQuantity = getProductAvailableQuantity(product);
        const currentQuantity = parseInt(existingItem.quantity, 10) || 0;
        nextLineItems[existingIndex] = {
          ...existingItem,
          quantity: availableQuantity ? Math.min(currentQuantity + quantity, availableQuantity) : currentQuantity + quantity
        };
      } else {
        nextLineItems.push(lineItem);
      }
    });

    formik.setFieldValue('lineItems', nextLineItems);
    resetItemPicker();
  };

  const handleRemoveItem = (index) => {
    formik.setFieldValue('lineItems', lineItems.filter((_, i) => i !== index));
  };

  const handleNewAddressChange = (field, value) => {
    setNewAddressForm((prev) => {
      if (field === 'country') {
        return { ...prev, country: value, state_province: '' };
      }

      if (field === 'contact_phone') {
        return { ...prev, contact_phone: formatPhoneNumber(value) };
      }

      if (field === 'postal_code') {
        return { ...prev, postal_code: String(value || '').toUpperCase() };
      }

      return { ...prev, [field]: value };
    });
  };

  const handleSaveNewAddress = async () => {
    if (!newAddressForm.name.trim() || !newAddressForm.street_address.trim() || !newAddressForm.city.trim()) {
      alert('Please fill in name, street address, and city');
      return;
    }
    try {
      // Create a CustomerAddress (destination), not a CompanyWarehouse (source)
      const response = await api.post('/shipping/addresses', {
        ...newAddressForm,
        company_id: companyId,
        address_type: 'shipping',
        is_active: true
      });
      const newAddr = response.data?.address || response.data?.data || response.data;
      // Refresh the addresses list
      const result = await shippingAPI.getCustomerAddresses(companyId);
      setDestinationAddresses(normalizeAddressResults(result));
      if (newAddr?.id) handleDestinationSelect(newAddr.id);
      setShowAddAddress(false);
      setNewAddressForm({ name: '', street_address: '', address_line_2: '', city: '', state_province: '', postal_code: '', country: 'Canada', contact_name: '', contact_phone: '', notes: '' });
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address');
    }
  };

  const availableStates = getStatesForCountry(newAddressForm.country);

  // --- Render ---
  return (
    <div className="space-y-6">

      {/* Implicit Source Warehouse */}
      {resolvedWarehouse && (
        <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg text-sm">
          <div className="flex items-center text-gray-600">
            <LuPackage className="h-4 w-4 mr-2 text-gray-400" />
            <span>{isPickup ? 'Pickup from' : 'Shipping from'}: <span className="font-medium text-gray-900">{resolvedWarehouse.name}</span></span>
            {resolvedWarehouse.city && (
              <span className="text-gray-400 ml-1">({resolvedWarehouse.city}, {resolvedWarehouse.state_province})</span>
            )}
          </div>
          {allWarehouses.length > 1 && (
            <button
              type="button"
              onClick={() => setShowWarehousePicker(!showWarehousePicker)}
              className="text-indigo-600 hover:text-indigo-800 text-sm"
            >
              Change
            </button>
          )}
        </div>
      )}

      {showWarehousePicker && (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Warehouse</label>
          <div className="space-y-1">
            {allWarehouses.map(wh => (
              <label key={wh.id} className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${resolvedWarehouse?.id === wh.id ? 'bg-indigo-50 border border-indigo-200' : ''}`}>
                <input
                  type="radio"
                  name="source_warehouse"
                  checked={resolvedWarehouse?.id === wh.id}
                  onChange={() => handleWarehouseChange(wh.id)}
                  className="mr-2"
                />
                <span className="text-sm">{wh.name} — {wh.city}, {wh.state_province}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Outbound Type */}
      <div>
        <h4 className="text-base font-medium text-gray-900 mb-1">Outbound Type</h4>
        <p className="text-xs text-gray-500 mb-3">
          Choose whether this request ships to a destination or is picked up from the warehouse.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { value: 'ship', label: 'Ship', desc: 'Send products to a destination address', icon: LuTruck },
            { value: 'pickup', label: 'Pickup', desc: 'Customer pickup from the selected warehouse', icon: LuPackage }
          ].map(({ value, label, desc, icon: Icon }) => (
            <label
              key={value}
              className={`flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                outboundType === value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                name="outboundType"
                value={value}
                checked={outboundType === value}
                onChange={() => handleOutboundTypeChange(value)}
                className="sr-only"
              />
              <Icon className={`h-5 w-5 mt-0.5 mr-3 ${outboundType === value ? 'text-indigo-600' : 'text-gray-400'}`} />
              <span>
                <span className="block text-sm font-medium text-gray-900">{label}</span>
                <span className="block text-xs text-gray-500">{desc}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Requested Ship/Pickup Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {isPickup ? 'Expected Pickup Date' : 'Requested Ship Date'}
        </label>
        <div className="mt-1">
          <DatePicker
            selected={formik.values.scheduledDate ? new Date(formik.values.scheduledDate) : null}
            onChange={(date) => formik.setFieldValue('scheduledDate', date)}
            minDate={new Date()}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            placeholderText={isPickup ? 'Select pickup date' : 'Select ship date'}
          />
        </div>
      </div>

      {/* Destination Address */}
      {!isPickup && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <LuMapPin className="inline h-4 w-4 mr-1" />
            Destination Address *
          </label>
          <select
            value={formik.values.destinationAddressId || ''}
            onChange={(e) => handleDestinationSelect(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="">Select destination address</option>
            {destinationAddresses.map(addr => (
              <option key={addr.id} value={addr.id}>
                {addr.name} — {addr.city}, {addr.state_province}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => setShowAddAddress(!showAddAddress)} className="mt-2 text-sm text-indigo-600 hover:text-indigo-800">
            + Add new address
          </button>
        </div>
      )}

      {/* New Address Form (collapsible) */}
      {!isPickup && showAddAddress && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-900">New Destination Address</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Address Name *</label>
              <input type="text" value={newAddressForm.name} onChange={(e) => setNewAddressForm(prev => ({ ...prev, name: e.target.value }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g., Main Office" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contact Name</label>
              <input type="text" value={newAddressForm.contact_name} onChange={(e) => setNewAddressForm(prev => ({ ...prev, contact_name: e.target.value }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contact Phone</label>
              <input type="text" value={newAddressForm.contact_phone} onChange={(e) => handleNewAddressChange('contact_phone', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Street Address *</label>
              <input type="text" value={newAddressForm.street_address} onChange={(e) => setNewAddressForm(prev => ({ ...prev, street_address: e.target.value }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Address Line 2</label>
              <input type="text" value={newAddressForm.address_line_2} onChange={(e) => setNewAddressForm(prev => ({ ...prev, address_line_2: e.target.value }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">City *</label>
              <input type="text" value={newAddressForm.city} onChange={(e) => setNewAddressForm(prev => ({ ...prev, city: e.target.value }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {newAddressForm.country === 'Canada' ? 'Province' : 'State'}
              </label>
              {availableStates.length > 0 ? (
                <select value={newAddressForm.state_province} onChange={(e) => setNewAddressForm(prev => ({ ...prev, state_province: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  <option value="">Select {newAddressForm.country === 'Canada' ? 'Province' : 'State'}</option>
                  {availableStates.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              ) : (
                <input type="text" value={newAddressForm.state_province} onChange={(e) => setNewAddressForm(prev => ({ ...prev, state_province: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {newAddressForm.country === 'Canada' ? 'Postal Code' : 'ZIP Code'}
              </label>
              <input type="text" value={newAddressForm.postal_code}
                placeholder={newAddressForm.country === 'Canada' ? 'A1A 1A1' : '12345'}
                onChange={(e) => handleNewAddressChange('postal_code', e.target.value.toUpperCase())}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
              <select value={newAddressForm.country} onChange={(e) => setNewAddressForm(prev => ({ ...prev, country: e.target.value, state_province: '' }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <button type="button" onClick={() => setShowAddAddress(false)} className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSaveNewAddress} className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700">Save Address</button>
          </div>
        </div>
      )}

      {/* Items Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-base font-medium text-gray-900">Items to Ship</h4>
          <button type="button" onClick={() => setShowAddItem(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
            <LuPlus className="mr-1 h-4 w-4" /> Add Item
          </button>
        </div>

        {lineItems.length > 0 ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Identifiers</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lineItems.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{item.productSku}</div>
                      <div className="text-sm text-gray-500">{item.productName}</div>
                    </td>
                    <td className="px-4 py-3">
                      <input type="number" min="1" value={item.quantity ?? ''}
                        onChange={(e) => {
                          const updated = [...lineItems];
                          updated[index] = { ...updated[index], quantity: e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0) };
                          formik.setFieldValue('lineItems', updated);
                        }}
                        className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <button
                        type="button"
                        onClick={async () => {
                          // Fetch available assets for this line item so the user can edit selections
                          if (item.productId) {
                            try {
                              const response = await api.get('/shipping/inventory/assets', {
                                params: { product_id: item.productId, warehouse_id: resolvedWarehouse?.id, company_id: companyId }
                              });
                              const data = response.data || {};
                              setEditingLineAvailableSerials((data.serials || []).map(s => ({ id: s.id, value: s.value, project: s.project })));
                              setEditingLineAvailableMacs((data.macs || []).map(m => ({ id: m.id, value: m.value, project: m.project })));
                            } catch {
                              setEditingLineAvailableSerials([]);
                              setEditingLineAvailableMacs([]);
                            }
                          }
                          setEditingLineIndex(index);
                          setEditingLineSerials([...(item.specificSerials || [])]);
                          setEditingLineMacs([...(item.specificMacs || [])]);
                          setEditingPickerSearch('');
                          // Auto-pick which tab to show based on available data
                          setEditingPickerType(
                            (item.specificSerials?.length > 0 || !item.specificMacs?.length) ? 'serial' : 'mac'
                          );
                        }}
                        className="hover:underline"
                      >
                        {item.specificSerials?.length > 0 && <span className="text-green-600 mr-2">{item.specificSerials.length} serials</span>}
                        {item.specificMacs?.length > 0 && <span className="text-green-600">{item.specificMacs.length} MACs</span>}
                        {!item.specificSerials?.length && !item.specificMacs?.length && (
                          item.projectFilter
                            ? <span className="text-indigo-600">Project: {item.projectFilter}</span>
                            : <span className="text-gray-400">Auto (FIFO)</span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => handleRemoveItem(index)} className="text-red-500 hover:text-red-700">
                        <LuTrash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
            No items added yet. Click "Add Item" to select products.
          </div>
        )}

        {/* Totals */}
        {lineItems.length > 0 && (
          <div className="mt-3 bg-gray-50 p-3 rounded-lg flex items-center space-x-6 text-sm">
            <span><span className="font-medium">Items:</span> {lineItems.reduce((s, i) => s + (i.quantity || 0), 0)}</span>
            {totalWeight > 0 && <span><span className="font-medium">Weight:</span> {totalWeight.toFixed(1)} lbs</span>}
            {totalVolume > 0 && <span><span className="font-medium">Volume:</span> {totalVolume.toFixed(1)} cu ft</span>}
          </div>
        )}
      </div>

      {/* Shipment Groups / Label Info (progressive — shown when user expands) */}
      {lineItems.length > 0 && (
        <div>
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => setShowGroups(!showGroups)}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900">
              {showGroups ? <LuChevronDown className="h-4 w-4 mr-1" /> : <LuChevronRight className="h-4 w-4 mr-1" />}
              <span className="font-medium">Shipping Label Details</span>
              <span className="text-gray-400 ml-2">(optional — for project routing labels)</span>
            </button>
            {!showGroups && (formik.values.shipmentGroups || []).length > 0 && (
              <span className="text-xs text-green-600">{(formik.values.shipmentGroups || []).length} label group(s)</span>
            )}
          </div>

          {showGroups && (() => {
            // Compute remaining qty per line item across all groups.
            // Each group's items consume from the pool; once a SKU is
            // fully allocated it can't be added to more groups.
            const groups = formik.values.shipmentGroups || [];
            const getAssignedQty = (lineIdx, excludeGroupIdx) => {
              let total = 0;
              groups.forEach((g, gIdx) => {
                if (gIdx === excludeGroupIdx) return;
                (g.items || []).forEach(gi => {
                  if (gi.lineIndex === lineIdx) total += (parseInt(gi.quantity, 10) || 0);
                });
              });
              return total;
            };

            const updateGroup = (index, changes) => {
              const updated = [...groups];
              updated[index] = { ...updated[index], ...changes };
              formik.setFieldValue('shipmentGroups', updated);
            };

            return (
            <div className="mt-3 space-y-3">
              {groups.map((group, index) => {
                const groupItems = group.items || [];

                return (
                <div key={index} className="border border-gray-200 rounded-lg">
                  {/* Collapsible header — always visible */}
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedGroups(prev => {
                      const next = new Set(prev);
                      next.has(index) ? next.delete(index) : next.add(index);
                      return next;
                    })}
                  >
                    <div className="flex items-center gap-2">
                      {expandedGroups.has(index)
                        ? <LuChevronDown className="h-4 w-4 text-gray-400" />
                        : <LuChevronRight className="h-4 w-4 text-gray-400" />}
                      <span className="text-sm font-medium text-gray-700">Label Group {index + 1}</span>
                      {!expandedGroups.has(index) && (
                        <span className="text-xs text-gray-500 ml-2">
                          {[group.project_name, group.room_location, group.recipient_name].filter(Boolean).join(' / ') || 'No details'}
                          {groupItems.length > 0 && ` — ${groupItems.reduce((s, gi) => s + (parseInt(gi.quantity, 10) || 0), 0)} units`}
                        </span>
                      )}
                    </div>
                    <button type="button" onClick={(e) => {
                      e.stopPropagation();
                      const updated = [...groups];
                      updated.splice(index, 1);
                      formik.setFieldValue('shipmentGroups', updated);
                      // Rebuild the expanded set with shifted indices so
                      // groups after the deleted one stay in their correct
                      // expanded/collapsed state.
                      setExpandedGroups(prev => {
                        const next = new Set();
                        for (const i of prev) {
                          if (i < index) next.add(i);
                          else if (i > index) next.add(i - 1);
                        }
                        return next;
                      });
                    }} className="text-red-500 hover:text-red-700">
                      <LuTrash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Expanded content */}
                  {expandedGroups.has(index) && <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Project Code</label>
                      <input type="text" list={datalistId + '-code'} value={group.project_code || ''} onChange={(e) => {
                        const val = e.target.value;
                        fetchProjectSuggestions(val);
                        const match = projectSuggestions.find(p => p.project_code === val);
                        if (match) {
                          updateGroup(index, { project_code: val, project_name: match.project_name || group.project_name });
                        } else {
                          updateGroup(index, { project_code: val });
                        }
                      }}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g., HARVARD001" />
                      <datalist id={datalistId + '-code'}>
                        {projectSuggestions.filter(p => p.project_code).map((p, i) => (
                          <option key={i} value={p.project_code}>{p.project_name ? `${p.project_code} - ${p.project_name}` : p.project_code}</option>
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Project Name</label>
                      <input type="text" list={datalistId + '-name'} value={group.project_name || ''} onChange={(e) => {
                        const val = e.target.value;
                        fetchProjectSuggestions(val);
                        const match = projectSuggestions.find(p => p.project_name === val);
                        if (match) {
                          updateGroup(index, { project_name: val, project_code: match.project_code || group.project_code });
                        } else {
                          updateGroup(index, { project_name: val });
                        }
                      }}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g., Harvard Library Deployment" />
                      <datalist id={datalistId + '-name'}>
                        {projectSuggestions.filter(p => p.project_name).map((p, i) => (
                          <option key={i} value={p.project_name}>{p.project_code ? `${p.project_name} - ${p.project_code}` : p.project_name}</option>
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Room / Location</label>
                      <input type="text" value={group.room_location || ''} onChange={(e) => updateGroup(index, { room_location: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g., Room 204, Building A" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Recipient Name</label>
                      <input type="text" value={group.recipient_name || ''} onChange={(e) => updateGroup(index, { recipient_name: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="If different from ship-to contact" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Label Notes</label>
                      <input type="text" value={group.recipient_notes || ''} onChange={(e) => updateGroup(index, { recipient_notes: e.target.value })}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Printed on the shipping label" />
                    </div>

                    {/* Per-group line items with quantity allocation */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-2">Items in This Group</label>
                      {groupItems.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {groupItems.map((gi, giIdx) => {
                            const srcItem = lineItems[gi.lineIndex];
                            if (!srcItem) return null;
                            // Max for this item in this group: shipping qty minus
                            // what other groups already claimed
                            const othersAssigned = getAssignedQty(gi.lineIndex, index);
                            const shippingQty = parseInt(srcItem.quantity, 10) || 0;
                            const maxForThisGroup = shippingQty - othersAssigned;
                            return (
                              <div key={giIdx} className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2">
                                <span className="text-sm font-medium text-indigo-800 flex-1">{srcItem.productSku}</span>
                                <input
                                  type="number" min="1" max={maxForThisGroup}
                                  value={gi.quantity ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 0);
                                    const updatedItems = [...groupItems];
                                    updatedItems[giIdx] = { ...updatedItems[giIdx], quantity: val };
                                    updateGroup(index, { items: updatedItems });
                                  }}
                                  className="w-20 rounded-md border-indigo-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500 text-center"
                                />
                                <span className="text-xs text-indigo-600">of {shippingQty} shipping</span>
                                <button type="button" onClick={async () => {
                                  // Open group-level asset picker
                                  if (srcItem.productId) {
                                    try {
                                      const response = await api.get('/shipping/inventory/assets', {
                                        params: { product_id: srcItem.productId, warehouse_id: resolvedWarehouse?.id, company_id: companyId }
                                      });
                                      const data = response.data || {};
                                      setGroupPickerAvailableSerials((data.serials || []).map(s => ({ id: s.id, value: s.value, project: s.project })));
                                      setGroupPickerAvailableMacs((data.macs || []).map(m => ({ id: m.id, value: m.value, project: m.project })));
                                    } catch {
                                      setGroupPickerAvailableSerials([]);
                                      setGroupPickerAvailableMacs([]);
                                    }
                                  }
                                  setGroupPickerSerials([...(gi.serial_numbers || [])]);
                                  setGroupPickerMacs([...(gi.mac_addresses || [])]);
                                  setGroupPickerSearch('');
                                  setEditingPickerType('serial');
                                  setGroupAssetPicker({ groupIndex: index, giIdx, lineIndex: gi.lineIndex });
                                }} className="text-indigo-500 hover:text-indigo-700 text-xs whitespace-nowrap">
                                  {(gi.serial_numbers?.length > 0 || gi.mac_addresses?.length > 0)
                                    ? `${(gi.serial_numbers?.length || 0) + (gi.mac_addresses?.length || 0)} assigned`
                                    : 'Assign Units'}
                                </button>
                                <button type="button" onClick={() => {
                                  const updatedItems = groupItems.filter((_, i) => i !== giIdx);
                                  updateGroup(index, { items: updatedItems });
                                }} className="text-red-400 hover:text-red-600">
                                  <LuTrash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Add item dropdown — only show SKUs with remaining qty */}
                      {(() => {
                        const availableItems = lineItems
                          .map((item, idx) => {
                            const assigned = getAssignedQty(idx, -1);
                            const total = parseInt(item.quantity, 10) || 0;
                            const remaining = total - assigned;
                            const alreadyInGroup = groupItems.some(gi => gi.lineIndex === idx);
                            return { item, idx, remaining, alreadyInGroup };
                          })
                          .filter(a => a.remaining > 0 && !a.alreadyInGroup);

                        return availableItems.length > 0 ? (
                          <select
                            value=""
                            onChange={(e) => {
                              const lineIndex = parseInt(e.target.value, 10);
                              if (Number.isFinite(lineIndex)) {
                                const available = availableItems.find(a => a.idx === lineIndex);
                                const updatedItems = [...groupItems, { lineIndex, quantity: available?.remaining || 1 }];
                                updateGroup(index, { items: updatedItems });
                              }
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-500"
                          >
                            <option value="">+ Add Item to Group...</option>
                            {availableItems.map(a => (
                              <option key={a.idx} value={a.idx}>
                                {a.item.productSku} — {a.remaining} available
                              </option>
                            ))}
                          </select>
                        ) : groupItems.length === 0 ? (
                          <p className="text-xs text-gray-400">No items to ship have been added yet, or all quantities are assigned to other groups.</p>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  </div>}
                </div>
                );
              })}

              <button type="button" onClick={() => {
                const newIndex = groups.length;
                formik.setFieldValue('shipmentGroups', [...groups, {
                  project_code: '', project_name: '', room_location: '', recipient_name: '', recipient_notes: '', items: []
                }]);
                setExpandedGroups(prev => new Set(prev).add(newIndex));
              }} className="inline-flex items-center px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50">
                <LuPlus className="h-3.5 w-3.5 mr-1" /> Add Label Group
              </button>
            </div>
            );
          })()}
        </div>
      )}

      {/* Shipping Method (shown after items added) */}
      {lineItems.length > 0 && !isPickup && (
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-1">Shipping Method</h4>
          <p className="text-xs text-gray-500 mb-3">Choose whether this outbound request ships by courier, freight, or customer pickup.</p>

          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'courier', label: 'Courier', desc: 'Parcel shipping and labels', icon: LuBox },
              { value: 'freight', label: 'Freight', desc: 'LTL / palletized shipping', icon: LuTruck },
              { value: 'pickup', label: 'Pickup', desc: '$0.00 customer pickup', icon: LuPackage }
            ].map(({ value, label, desc, icon: Icon }) => (
              <label key={value} className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 text-center ${formik.values.shippingMethod === value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                <input type="radio" name="shippingMethod" value={value} checked={formik.values.shippingMethod === value}
                  onChange={(e) => handleShippingMethodChange(e.target.value)} className="sr-only" />
                <Icon className={`h-5 w-5 mb-1 ${formik.values.shippingMethod === value ? 'text-indigo-600' : 'text-gray-400'}`} />
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-gray-500">{desc}</div>
              </label>
            ))}
          </div>

          {isFreight && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              LTL freight costs are calculated after the pallet is built and ready. Final freight costs will be provided once ready.
            </div>
          )}

          {/* Accessorials apply to freight/LTL only. */}
          {isFreight && <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-gray-600 mb-2">Special Requirements (optional)</p>
            <div className="space-y-2">
              <label className="flex items-center text-sm">
                <input type="checkbox" checked={formik.values.liftgateRequired || false}
                  onChange={(e) => formik.setFieldValue('liftgateRequired', e.target.checked)} className="mr-2 rounded" />
                Liftgate Required
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" checked={formik.values.whiteGlove || false}
                  onChange={(e) => formik.setFieldValue('whiteGlove', e.target.checked)} className="mr-2 rounded" />
                White Glove Service
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" checked={formik.values.inside_delivery || false}
                  onChange={(e) => formik.setFieldValue('inside_delivery', e.target.checked)} className="mr-2 rounded" />
                Inside Delivery
              </label>
            </div>
          </div>}

        </div>
      )}

      {isPickup && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Customer pickup requests do not require a destination address and do not generate carrier rates or shipping labels.
        </div>
      )}

      {/* Internal Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Internal Notes (Optional)</label>
        <textarea rows="3" value={formik.values.specialInstructions || ''}
          onChange={(e) => formik.setFieldValue('specialInstructions', e.target.value)}
          className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Notes for warehouse staff (not visible on packing slips or labels)" />
      </div>

      {/* Add Item Modal */}
      {showAddItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Add Items to Shipment</h3>
              <p className="text-sm text-gray-500">Select one or more SKUs, set quantities, then finish details on the request.</p>
            </div>
            <div className="px-6 py-4 border-b border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search Products</label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative">
                  <LuSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input type="text" placeholder="Search by SKU or name..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 block w-full sm:w-96 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
                {searchTerm.length === 1 && (
                  <span className="text-xs text-gray-500">Enter at least 2 characters to search.</span>
                )}
                {Object.keys(pickerSelections).length > 0 && (
                  <span className="text-xs font-medium text-indigo-700">
                    {Object.keys(pickerSelections).length} selected
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {(!productSuggestions || productSuggestions.length === 0) ? (
                <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center text-sm text-gray-500">
                  {searchTerm.length === 1 ? 'Keep typing to search products.' : 'No matching inventory items found.'}
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-12 px-4 py-2"></th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">SKU</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Item</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Available</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {productSuggestions.map(product => {
                        const selected = pickerSelections[product.id];
                        const availableQuantity = getProductAvailableQuantity(product);
                        const isDisabled = availableQuantity <= 0;

                        return (
                          <tr key={product.id} className={selected ? 'bg-indigo-50' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={!!selected}
                                disabled={isDisabled}
                                onChange={() => togglePickerSelection(product)}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                disabled={isDisabled}
                                onClick={() => togglePickerSelection(product)}
                                className="text-left text-sm font-medium text-gray-900 disabled:text-gray-400"
                              >
                                {product.productSku}
                                {product.internalSku && <span className="ml-1 text-xs font-normal text-gray-500">({product.internalSku})</span>}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{product.productName || 'Unnamed item'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{availableQuantity}</td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max={availableQuantity || 0}
                                value={selected?.quantity ?? ''}
                                disabled={!selected || isDisabled}
                                onChange={(e) => updatePickerQuantity(product, e.target.value)}
                                className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 sm:text-sm"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button type="button" onClick={resetItemPicker}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={handleAddSelectedItems} disabled={Object.values(pickerSelections).every(({ quantity }) => !quantity || quantity <= 0)}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                Add Selected Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Serial Number Picker Modal */}
      {showSerialPicker && (() => {
        const available = filteredSerials.filter(s =>
          !selectedSerials.includes(s.value) &&
          (!pickerSearch || s.value.toLowerCase().includes(pickerSearch.toLowerCase()))
        );
        const selected = filteredSerials.filter(s => selectedSerials.includes(s.value));

        return (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Select Serial Numbers</h3>
                <p className="text-sm text-gray-500">{currentItem.productSku}</p>
              </div>

              <div className="px-5 py-3 border-b border-gray-100">
                <div className="relative">
                  <LuSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input type="text" placeholder="Search serial numbers..." value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-3">
                {/* Selected */}
                {selected.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Selected ({selected.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.map(s => (
                        <button key={s.id} type="button"
                          onClick={() => setSelectedSerials(selectedSerials.filter(v => v !== s.value))}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors">
                          {s.value}
                          <span className="ml-1.5 text-indigo-400 hover:text-red-500">&times;</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available */}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Available ({available.length})</div>
                  {available.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {available.map(s => (
                        <button key={s.id} type="button"
                          onClick={() => setSelectedSerials([...selectedSerials, s.value])}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-gray-50 text-gray-700 border border-gray-200 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors">
                          {s.value}
                          {s.project && <span className="ml-1.5 text-gray-400 text-[10px]">{s.project}</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">{pickerSearch ? 'No matches' : 'All selected'}</p>
                  )}
                </div>
              </div>

              <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm text-gray-500">{selectedSerials.length} selected</span>
                <div className="space-x-2">
                  <button type="button" onClick={() => { setShowSerialPicker(false); setPickerSearch(''); }}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button type="button" onClick={() => {
                    // Resolve selected asset IDs from available serials
                    const serialAssetIds = filteredSerials.filter(s => selectedSerials.includes(s.value)).map(s => s.id);
                    const existingMacIds = filteredMacs.filter(m => currentItem.specificMacs.includes(m.value)).map(m => m.id);
                    setCurrentItem({
                      ...currentItem,
                      specificSerials: selectedSerials,
                      specificAssetIds: [...new Set([...serialAssetIds, ...existingMacIds])]
                    });
                    setShowSerialPicker(false);
                    setPickerSearch('');
                  }}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700">Confirm</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* MAC Address Picker Modal */}
      {showMacPicker && (() => {
        const available = filteredMacs.filter(m =>
          !selectedMacs.includes(m.value) &&
          (!pickerSearch || m.value.toLowerCase().includes(pickerSearch.toLowerCase()))
        );
        const selected = filteredMacs.filter(m => selectedMacs.includes(m.value));

        return (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Select MAC Addresses</h3>
                <p className="text-sm text-gray-500">{currentItem.productSku}</p>
              </div>

              <div className="px-5 py-3 border-b border-gray-100">
                <div className="relative">
                  <LuSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input type="text" placeholder="Search MAC addresses..." value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-3">
                {/* Selected */}
                {selected.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Selected ({selected.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.map(m => (
                        <button key={m.id} type="button"
                          onClick={() => setSelectedMacs(selectedMacs.filter(v => v !== m.value))}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors">
                          {m.value}
                          <span className="ml-1.5 text-indigo-400 hover:text-red-500">&times;</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Available */}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Available ({available.length})</div>
                  {available.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {available.map(m => (
                        <button key={m.id} type="button"
                          onClick={() => setSelectedMacs([...selectedMacs, m.value])}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-gray-50 text-gray-700 border border-gray-200 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors">
                          {m.value}
                          {m.project && <span className="ml-1.5 text-gray-400 text-[10px]">{m.project}</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">{pickerSearch ? 'No matches' : 'All selected'}</p>
                  )}
                </div>
              </div>

              <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm text-gray-500">{selectedMacs.length} selected</span>
                <div className="space-x-2">
                  <button type="button" onClick={() => { setShowMacPicker(false); setPickerSearch(''); }}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button type="button" onClick={() => {
                    const macAssetIds = filteredMacs.filter(m => selectedMacs.includes(m.value)).map(m => m.id);
                    const existingSerialIds = filteredSerials.filter(s => currentItem.specificSerials.includes(s.value)).map(s => s.id);
                    setCurrentItem({
                      ...currentItem,
                      specificMacs: selectedMacs,
                      specificAssetIds: [...new Set([...existingSerialIds, ...macAssetIds])]
                    });
                    setShowMacPicker(false);
                    setPickerSearch('');
                  }}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700">Confirm</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Line Item SN/MAC Editor Modal */}
      {editingLineIndex !== null && (() => {
        const editItem = lineItems[editingLineIndex];
        if (!editItem) return null;
        const hasSerials = editingLineAvailableSerials.length > 0;
        const hasMacs = editingLineAvailableMacs.length > 0;
        if (!hasSerials && !hasMacs) {
          // No assets available -- nothing to pick
          return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <p className="text-sm text-gray-600 mb-4">No serial numbers or MAC addresses are available for this product. Allocation will use FIFO.</p>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setEditingLineIndex(null)}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Close</button>
                </div>
              </div>
            </div>
          );
        }

        const showType = editingPickerType || 'serial';
        const sourceList = showType === 'serial' ? editingLineAvailableSerials : editingLineAvailableMacs;
        const selectedList = showType === 'serial' ? editingLineSerials : editingLineMacs;
        const setSelectedList = showType === 'serial' ? setEditingLineSerials : setEditingLineMacs;

        const available = sourceList.filter(s =>
          !selectedList.includes(s.value) &&
          (!editingPickerSearch || s.value.toLowerCase().includes(editingPickerSearch.toLowerCase()))
        );
        const selected = sourceList.filter(s => selectedList.includes(s.value));

        return (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Select Units for {editItem.productSku}</h3>
                <p className="text-sm text-gray-500">Qty: {editItem.quantity} -- select specific units or leave empty for FIFO</p>
              </div>

              {/* Tab bar if both types available */}
              {hasSerials && hasMacs && (
                <div className="flex border-b border-gray-200">
                  <button type="button" onClick={() => { setEditingPickerType('serial'); setEditingPickerSearch(''); }}
                    className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 ${showType === 'serial' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    Serial Numbers ({editingLineSerials.length})
                  </button>
                  <button type="button" onClick={() => { setEditingPickerType('mac'); setEditingPickerSearch(''); }}
                    className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 ${showType === 'mac' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    MAC Addresses ({editingLineMacs.length})
                  </button>
                </div>
              )}

              <div className="px-5 py-3 border-b border-gray-100">
                <div className="relative">
                  <LuSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input type="text" placeholder={`Search ${showType === 'serial' ? 'serial numbers' : 'MAC addresses'}...`}
                    value={editingPickerSearch} onChange={(e) => setEditingPickerSearch(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-3">
                {selected.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Selected ({selected.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.map(s => (
                        <button key={s.id} type="button"
                          onClick={() => setSelectedList(selectedList.filter(v => v !== s.value))}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors">
                          {s.value}
                          <span className="ml-1.5 text-indigo-400 hover:text-red-500">&times;</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Available ({available.length})</div>
                  {available.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {available.map(s => (
                        <button key={s.id} type="button"
                          onClick={() => setSelectedList([...selectedList, s.value])}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-gray-50 text-gray-700 border border-gray-200 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors">
                          {s.value}
                          {s.project && <span className="ml-1.5 text-gray-400 text-[10px]">{s.project}</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">{editingPickerSearch ? 'No matches' : 'All selected'}</p>
                  )}
                </div>
              </div>

              <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  {editingLineSerials.length > 0 && <span className="mr-3">{editingLineSerials.length} serial(s)</span>}
                  {editingLineMacs.length > 0 && <span>{editingLineMacs.length} MAC(s)</span>}
                  {editingLineSerials.length === 0 && editingLineMacs.length === 0 && <span>None selected (FIFO)</span>}
                </div>
                <div className="space-x-2">
                  <button type="button" onClick={() => setEditingLineIndex(null)}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button type="button" onClick={() => {
                    const serialAssetIds = editingLineAvailableSerials.filter(s => editingLineSerials.includes(s.value)).map(s => s.id);
                    const macAssetIds = editingLineAvailableMacs.filter(m => editingLineMacs.includes(m.value)).map(m => m.id);
                    const updated = [...lineItems];
                    updated[editingLineIndex] = {
                      ...updated[editingLineIndex],
                      specificSerials: editingLineSerials,
                      specificMacs: editingLineMacs,
                      specificAssetIds: [...new Set([...serialAssetIds, ...macAssetIds])]
                    };
                    formik.setFieldValue('lineItems', updated);
                    setEditingLineIndex(null);
                  }}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700">Confirm</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Group-Level Asset Assignment Modal */}
      {groupAssetPicker && (() => {
        const { groupIndex, giIdx, lineIndex } = groupAssetPicker;
        const groups = formik.values.shipmentGroups || [];
        const group = groups[groupIndex];
        const gi = group?.items?.[giIdx];
        const srcItem = lineItems[lineIndex];
        if (!gi || !srcItem) return null;

        const hasSerials = groupPickerAvailableSerials.length > 0;
        const hasMacs = groupPickerAvailableMacs.length > 0;
        if (!hasSerials && !hasMacs) {
          return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <p className="text-sm text-gray-600 mb-4">No serial numbers or MAC addresses are available for this product.</p>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setGroupAssetPicker(null)}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Close</button>
                </div>
              </div>
            </div>
          );
        }

        // Filter out SNs/MACs already assigned to OTHER groups for the same line item
        const otherGroupAssigned = { serials: new Set(), macs: new Set() };
        groups.forEach((g, gIdx) => {
          if (gIdx === groupIndex) return;
          (g.items || []).forEach(otherGi => {
            if (otherGi.lineIndex === lineIndex) {
              (otherGi.serial_numbers || []).forEach(sn => otherGroupAssigned.serials.add(sn));
              (otherGi.mac_addresses || []).forEach(mac => otherGroupAssigned.macs.add(mac));
            }
          });
        });

        // If the parent line item has specific selections, restrict to those
        const parentSerials = srcItem.specificSerials?.length > 0 ? new Set(srcItem.specificSerials) : null;
        const parentMacs = srcItem.specificMacs?.length > 0 ? new Set(srcItem.specificMacs) : null;

        const filteredAvailSerials = groupPickerAvailableSerials
          .filter(s => !otherGroupAssigned.serials.has(s.value))
          .filter(s => !parentSerials || parentSerials.has(s.value));
        const filteredAvailMacs = groupPickerAvailableMacs
          .filter(m => !otherGroupAssigned.macs.has(m.value))
          .filter(m => !parentMacs || parentMacs.has(m.value));

        const showType = editingPickerType || 'serial';
        const sourceList = showType === 'serial' ? filteredAvailSerials : filteredAvailMacs;
        const selectedList = showType === 'serial' ? groupPickerSerials : groupPickerMacs;
        const setSelectedList = showType === 'serial' ? setGroupPickerSerials : setGroupPickerMacs;

        const available = sourceList.filter(s =>
          !selectedList.includes(s.value) &&
          (!groupPickerSearch || s.value.toLowerCase().includes(groupPickerSearch.toLowerCase()))
        );
        const selected = sourceList.filter(s => selectedList.includes(s.value));

        return (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Assign Units -- Group {groupIndex + 1}</h3>
                <p className="text-sm text-gray-500">{srcItem.productSku} -- qty {gi.quantity}</p>
              </div>

              {filteredAvailSerials.length > 0 && filteredAvailMacs.length > 0 && (
                <div className="flex border-b border-gray-200">
                  <button type="button" onClick={() => { setEditingPickerType('serial'); setGroupPickerSearch(''); }}
                    className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 ${showType === 'serial' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    Serial Numbers ({groupPickerSerials.length})
                  </button>
                  <button type="button" onClick={() => { setEditingPickerType('mac'); setGroupPickerSearch(''); }}
                    className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 ${showType === 'mac' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    MAC Addresses ({groupPickerMacs.length})
                  </button>
                </div>
              )}

              <div className="px-5 py-3 border-b border-gray-100">
                <div className="relative">
                  <LuSearch className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input type="text" placeholder={`Search ${showType === 'serial' ? 'serial numbers' : 'MAC addresses'}...`}
                    value={groupPickerSearch} onChange={(e) => setGroupPickerSearch(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-3">
                {selected.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Selected ({selected.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.map(s => (
                        <button key={s.id} type="button"
                          onClick={() => setSelectedList(selectedList.filter(v => v !== s.value))}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors">
                          {s.value}
                          <span className="ml-1.5 text-indigo-400 hover:text-red-500">&times;</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Available ({available.length})</div>
                  {available.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {available.map(s => (
                        <button key={s.id} type="button"
                          onClick={() => setSelectedList([...selectedList, s.value])}
                          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-gray-50 text-gray-700 border border-gray-200 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-colors">
                          {s.value}
                          {s.project && <span className="ml-1.5 text-gray-400 text-[10px]">{s.project}</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">{groupPickerSearch ? 'No matches' : 'All selected'}</p>
                  )}
                </div>
              </div>

              <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  {groupPickerSerials.length > 0 && <span className="mr-3">{groupPickerSerials.length} serial(s)</span>}
                  {groupPickerMacs.length > 0 && <span>{groupPickerMacs.length} MAC(s)</span>}
                  {groupPickerSerials.length === 0 && groupPickerMacs.length === 0 && <span>None assigned</span>}
                </div>
                <div className="space-x-2">
                  <button type="button" onClick={() => setGroupAssetPicker(null)}
                    className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                  <button type="button" onClick={() => {
                    const serialAssetIds = filteredAvailSerials.filter(s => groupPickerSerials.includes(s.value)).map(s => s.id);
                    const macAssetIds = filteredAvailMacs.filter(m => groupPickerMacs.includes(m.value)).map(m => m.id);
                    const updatedGroups = [...groups];
                    const updatedItems = [...(updatedGroups[groupIndex].items || [])];
                    updatedItems[giIdx] = {
                      ...updatedItems[giIdx],
                      serial_numbers: groupPickerSerials.length > 0 ? groupPickerSerials : undefined,
                      mac_addresses: groupPickerMacs.length > 0 ? groupPickerMacs : undefined,
                      asset_ids: [...new Set([...serialAssetIds, ...macAssetIds])]
                    };
                    updatedGroups[groupIndex] = { ...updatedGroups[groupIndex], items: updatedItems };
                    formik.setFieldValue('shipmentGroups', updatedGroups);
                    setGroupAssetPicker(null);
                  }}
                    className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700">Confirm</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ShippingForm;
